/**
 * SFXManager
 *
 * Central audio manager. Loads all sounds on init, plays them by key.
 * All sounds respect a master volume (0.0 – 1.0).
 *
 * Usage:
 *   sfx.play('hammerHit')
 *   sfx.play('alchemistAttack', { volume: 0.3 })  // quieter
 *   sfx.setVolume(0.5)
 */
export class SFXManager {

    constructor() {
        this.sounds      = new Map();
        this.masterVol   = 1.0;
        this.poolSize    = 4;
        this.lastPlayed  = new Map(); // key → timestamp (ms) of last play

        // Minimum ms between plays of the same sound.
        // Override per key in options: { cooldown: 200 }
        this.defaultCooldowns = {
            death:                  80,   // many enemies can die at once
            hammerHit:              100,
            mageAttack:             100,
            phantomAttack:          20000,
            necromancerAttack:      150,
            necromancerKill:        10000,
            necromancerSummon:      300,
            necromancerSummonAlt:   300,
            alchemistAttack:        150,
            farmCollect:            500,
            waveStart:              1000,
            frostBitePower:         1000,
            abyssRift:              1000,
            alchemistAbility:       1000,
            finalBossArrives:       5000,
            bardSpawn:              500,
            upgradeTo2:             300,
            upgradeTo3:             300,
            upgradeTo4:             300,
            upgradeTo5:             300,
        };
    }

    // ─── Init ─────────────────────────────────────────────────────────────────

    async init() {
        const sounds = [
            { key: 'abyssRift',                 path: '/Assets/Sounds/AbyssRift.wav',                  baseVol: 1.0  },
            { key: 'alchemistAbility',           path: '/Assets/Sounds/AlchemistAbility.mp3',           baseVol: 1.0  },
            { key: 'alchemistAttack',            path: '/Assets/Sounds/AlchemistAttack.mp3',            baseVol: 0.3  },
            { key: 'bardSpawn',                  path: '/Assets/Sounds/BardSpaw.mp3',                   baseVol: 1.0  },
            { key: 'death',                      path: '/Assets/Sounds/Death.wav',                      baseVol: 0.6  },
            { key: 'farmCollect',                path: '/Assets/Sounds/FarmCollect.wav',                baseVol: 1.0  },
            { key: 'finalBossArrives',           path: '/Assets/Sounds/FinalBossArrives.mp3',           baseVol: 1.0  },
            { key: 'frostBitePower',             path: '/Assets/Sounds/FrostBitePower.mp3',             baseVol: 1.0  },
            { key: 'hammerHit',                  path: '/Assets/Sounds/HammerHit.wav',                  baseVol: 0.7  },
            { key: 'mageAttack',                 path: '/Assets/Sounds/MageAttack.wav',                 baseVol: 0.8  },
            { key: 'necromancerAttack',          path: '/Assets/Sounds/NecromancerAttack.mp3',          baseVol: 1.0  },
            { key: 'necromancerKill',            path: '/Assets/Sounds/NecromancerKill.mp3',            baseVol: 1.0  },
            { key: 'necromancerSummon',          path: '/Assets/Sounds/NecromancerSummon.mp3',          baseVol: 1.0  },
            { key: 'necromancerSummonAlt',       path: '/Assets/Sounds/NecromancerSummonAlternate.mp3', baseVol: 1.0  },
            { key: 'phantomAttack',              path: '/Assets/Sounds/PhantomAttack.mp3',              baseVol: 1.0  },
            { key: 'upgradeTo2',                 path: '/Assets/Sounds/UpgradeTo2.wav',                 baseVol: 1.0  },
            { key: 'upgradeTo3',                 path: '/Assets/Sounds/UpgradeTo3.wav',                 baseVol: 1.0  },
            { key: 'upgradeTo4',                 path: '/Assets/Sounds/UpgradeTo4.wav',                 baseVol: 1.0  },
            { key: 'upgradeTo5',                 path: '/Assets/Sounds/UpgradeTo5.wav',                 baseVol: 1.0  },
            { key: 'waveStart',                  path: '/Assets/Sounds/WaveStart.mp3',                  baseVol: 0.3  },
        ];

        sounds.forEach(({ key, path, baseVol }) => {
            // Create a pool of audio elements for each sound
            const pool = Array.from({ length: this.poolSize }, () => {
                const audio = new Audio(path);
                audio.preload    = 'auto';
                audio.dataset.baseVol = String(baseVol);
                audio.volume     = baseVol * this.masterVol;
                audio.addEventListener('error', () =>
                    console.warn(`SFXManager: failed to load "${key}" — ${path}`)
                );
                return audio;
            });
            this.sounds.set(key, pool);
        });
    }

    // ─── Playback ─────────────────────────────────────────────────────────────

    /**
     * Play a sound by key.
     * @param {string} key
     * @param {object} options  { volume: override 0-1 }
     */
    play(key, options = {}) {
        const pool = this.sounds.get(key);
        if (!pool) { console.warn(`SFXManager: "${key}" not loaded`); return; }

        // Cooldown check — prevent spam
        const cooldown = options.cooldown ?? this.defaultCooldowns[key] ?? 50;
        const now      = performance.now();
        const last     = this.lastPlayed.get(key) ?? 0;
        if (now - last < cooldown) return;
        this.lastPlayed.set(key, now);

        // Find a pool slot that is idle (ended or not started)
        const audio = pool.find(a => a.paused || a.ended) ?? pool[0];

        const baseVol     = parseFloat(audio.dataset.baseVol ?? '1');
        const overrideVol = options.volume ?? baseVol;
        audio.volume      = Math.min(1, overrideVol * this.masterVol);
        audio.currentTime = 0;
        audio.play().catch(() => {});
    }

    // ─── Volume ───────────────────────────────────────────────────────────────

    setVolume(vol) {
        this.masterVol = Math.max(0, Math.min(1, vol));
        // Update all currently loaded audio elements
        this.sounds.forEach(pool => {
            pool.forEach(audio => {
                const baseVol = parseFloat(audio.dataset.baseVol ?? '1');
                audio.volume  = baseVol * this.masterVol;
            });
        });
        // Persist to localStorage
        localStorage.setItem('sfx_volume', String(this.masterVol));
    }

    getVolume() { return this.masterVol; }

    loadVolume() {
        const saved = localStorage.getItem('sfx_volume');
        if (saved !== null) this.setVolume(parseFloat(saved));
    }

    destroy() {
        this.sounds.forEach(pool => pool.forEach(a => { a.pause(); a.src = ''; }));
        this.sounds.clear();
        this.lastPlayed.clear();
    }
}